<section
    aria-labelledby="automated-step-editor-title"
    class="activity-editor"
  >
    <header [hidden]="section() !== 'all' && section() !== 'activity'">
      <h4 id="automated-step-editor-title" tabindex="-1">Automated activity</h4>
      <button
        uiButton
        type="button"
        (click)="checkActivities()"
        [disabled]="interactionBlocked || loading"
      >
        Check installed activities
      </button>
    </header>
    <p [hidden]="section() !== 'all' && section() !== 'activity'">
      This step runs through the system worker. It has no staff form or
      assignment queue. Worker configuration and current scoped authority are
      checked separately at execution. Nightly candidate checks and starting new
      workflows are configured separately.
    </p>
    <p *ngIf="loading" role="status">Loading installed activities…</p>
    <p *ngIf="loadError" role="alert">{{ loadError }}</p>
    <p *ngIf="error" role="alert" tabindex="-1">{{ error }}</p>
    <p *ngIf="catalog" [hidden]="section() !== 'all' && section() !== 'activity'">{{ catalog.execution.message }}</p>
    <p *ngIf="catalog && !catalog.operations.length">
      No compatible automated activity is installed for this workflow and pack.
      Saved settings remain inspectable.
    </p>
    <label [hidden]="section() !== 'all' && section() !== 'activity'"
      >Registered activity<select
        uiControl #activity="ngModel"
        [ngModel]="operationKey"
        (ngModelChange)="selectOperation($event, activity)"
        [ngModelOptions]="standalone"
        [disabled]="disabled || !!automation && !!structureIssue"
      >
        <option *ngIf="!operation" [value]="operationKey">
          {{
            automation
              ? 'Unavailable retained activity: ' +
                (automation.command?.key?.code || 'unreadable exact reference')
              : 'Choose an installed activity'
          }}
        </option>
        <option
          *ngFor="let item of catalog?.operations"
          [value]="operationId(item.command)"
          [disabled]="!item.availableForNewUse"
        >
          {{ item.label }} · v{{ item.command.key.version
          }}{{ item.availableForNewUse ? '' : ' · unavailable for new use' }}
        </option>
      </select></label
    >
    <section
      *ngIf="unavailableActivities.length"
      class="activity-unavailable" [hidden]="section() !== 'all' && section() !== 'activity'"
      aria-label="Unavailable activities"
      role="status"
    >
      <strong>Unavailable activities</strong>
      <ul>
        <li *ngFor="let activity of unavailableActivities">
          <strong>{{ activity.label }}</strong
          >:
          {{
            activity.unavailableReason ||
              'This activity is unavailable for new use.'
          }}
        </li>
      </ul>
    </section>
    <ng-container *ngIf="operation as selected; else retained">
      <p [hidden]="section() !== 'all' && section() !== 'activity'" *ngIf="isHearingEligibility" data-testid="eligibility-activity-help">
        Calculates eligibility for the hearing linked to this workflow using
        server-selected person facts and policy. No facts or policy values are
        entered here. An administrator must configure the eligibility data
        source and worker before this activity can run.
      </p>
      <div [hidden]="section() !== 'all' && section() !== 'activity'" *ngIf="isHearingEligibility && document.subjectTypeCode !== 'hearing_cycle'"
        role="alert" data-testid="eligibility-lifecycle-required">
        <p>
          Before publishing, use Hearing as this workflow’s subject in Workflow
          settings → Hearing lifecycle. Hearing enrollment creates the primary
          lifecycle link for each run. A Hearing reference alone does not make
          this the hearing’s main lifecycle workflow.
        </p>
        <button uiButton type="button" (click)="openHearingSettings()"
          [disabled]="interactionBlocked">Open hearing lifecycle settings</button>
      </div>
      <p *ngIf="automation?.version !== 1" role="alert">
        This retained activity contract version is unavailable for editing.
        Saved values are preserved.
      </p>
      <section
        *ngFor="let field of selected.input.fields; trackBy: fieldKey"
        class="activity-input" [attr.data-automated-input]="field.name"
        [hidden]="section() !== 'all' && section() !== 'activity'"
      >
        <h5>
          {{ readableName(field.name)
          }}{{ field.required ? ' (required)' : '' }}
        </h5>
        <label
          >Value source<select
            uiControl #source="ngModel" data-automated-source
            [ngModel]="input(field.name)?.source || 'omitted'"
            (ngModelChange)="setSource(field, $event, source)"
            [ngModelOptions]="standalone"
            [disabled]="settingsDisabled"
          >
            <option *ngIf="!field.required" value="omitted">
              Do not supply
            </option>
            <option
              *ngIf="field.required && !input(field.name)"
              value="omitted"
            >
              Choose a source
            </option>
            <option value="literal">Configured value</option>
            <option value="context">Captured process context</option>
            <option
              *ngIf="
                input(field.name) &&
                !['literal', 'context'].includes(input(field.name)!.source)
              "
              [value]="input(field.name)!.source"
            >
              Unavailable retained source
            </option>
          </select></label
        >
        <ng-container *ngIf="input(field.name)?.source === 'literal'">
          <ng-container *ngIf="usesExactLiteral(field); else ordinaryLiteral">
            <label
              *ngIf="
                field.type === 'number' || field.type === 'integer';
                else complexLiteral
              "
              >Exact configured value<input
                uiControl #exact="ngModel" data-automated-literal
                type="text"
                inputmode="decimal"
                maxlength="128"
                [attr.aria-label]="field.name"
                [ngModel]="exactLiteral(field.name)"
                (ngModelChange)="setExactLiteral(field.name, $event, field, exact)"
                [ngModelOptions]="standalone"
                [disabled]="settingsDisabled"
              />
            </label>
            <ng-template #complexLiteral>
              <label
                >Exact configured JSON<textarea
                  uiControl #exact="ngModel" data-automated-literal
                  rows="4"
                  [attr.aria-label]="field.name"
                  [ngModel]="exactLiteral(field.name)"
                  (ngModelChange)="setExactLiteral(field.name, $event, field, exact)"
                  [ngModelOptions]="standalone"
                  [disabled]="settingsDisabled"
                ></textarea>
              </label>
            </ng-template>
            <p class="input-hint">
              Values are kept exactly as entered. The registered input contract
              is checked before publication.
            </p>
          </ng-container>
          <ng-template #ordinaryLiteral>
            <phms-typed-capability-inputs data-automated-literal
              [fields]="singleField(field)"
              [values]="literal(field.name)"
              [disabled]="settingsDisabled"
              (valuesChange)="setLiteral(field.name, $event, field)"
            />
          </ng-template>
        </ng-container>
        <label *ngIf="input(field.name)?.source === 'context'"
          >Captured value<select
            uiControl #contextModel="ngModel" data-automated-context
            [ngModel]="input(field.name)?.context || ''"
            (ngModelChange)="setContext(field.name, $event, field, contextModel)"
            [ngModelOptions]="standalone"
            [disabled]="settingsDisabled"
          >
            <option
              *ngIf="!contextAvailable(field)"
              [value]="input(field.name)?.context || ''"
            >
              {{
                input(field.name)?.context
                  ? 'Unavailable retained context: ' +
                    input(field.name)?.context
                  : 'Choose captured context'
              }}
            </option>
            <option
              *ngFor="let context of contexts(field)"
              [value]="context.code"
            >
              {{ context.label }}
            </option>
          </select></label
        >
      </section>
      <details [hidden]="section() !== 'all' && section() !== 'activity'">
        <summary>Declared result and exact activity contract</summary>
        <p>
          Module {{ selected.moduleCode }} v{{ selected.moduleVersion }} ·
          {{ selected.moduleHash }}
        </p>
        <p>
          Command {{ selected.command.key.provider }} /
          {{ selected.command.key.code }} v{{ selected.command.key.version }} ·
          {{ selected.command.contractHash }}
        </p>
        <ul>
          <li *ngFor="let field of selected.output.fields">
            {{ field.name }} · {{ field.type
            }}{{ field.required ? ' · required' : '' }}
          </li>
        </ul>
        <p>
          {{ selected.authorizationPolicy }} ·
          {{ selected.automationPermissionCode }}
        </p>
      </details>
    </ng-container>
    <ng-template #retained>
      <pre *ngIf="automation">{{ automation | json }}</pre>
    </ng-template>
    <section class="automated-routing" [hidden]="section() !== 'all' && section() !== 'routing'" aria-label="Automated activity routes">
      <header>
        <h4 tabindex="-1">Routes</h4>
        <button uiButton type="button" (click)="showSelectedPath()"
          [disabled]="interactionBlocked || !selectedResult || (!pending && !hasSavedPath(selectedResult))">View on process map</button>
      </header>
      <p>When this activity finishes, continue according to its result. The worker completes this step and follows its path.</p>
      <p *ngIf="missingDestinationCount" role="status">{{ missingDestinationCount }} {{ missingDestinationCount === 1 ? 'result needs' : 'results need' }} a destination.</p>
      <p *ngIf="structureIssue" role="status">{{ structureIssue }}</p>
      <p *ngIf="!operation" role="status">Exact installed activity metadata is unavailable or ambiguous. Saved results and paths remain inspectable.</p>
      <div class="activity-results-workspace">
        <ng-template #resultListHeader><h5>Activity results <span>{{ resultRows.length }}</span></h5></ng-template>
        <ui-panel [headerTemplate]="resultListHeader" [noGrow]="true" class="activity-results-panel">
        <nav class="activity-results" aria-label="Activity results">
          <button uiButton [wrap]="true" [text]="true" [outlined]="false" type="button" *ngFor="let row of resultRows; trackBy: trackResult" class="activity-result"
            [attr.data-automated-result]="row.commandOutcome" [attr.data-automated-route-outcome]="row.outcome?.routeOutcome"
            [attr.aria-label]="readableName(row.commandOutcome) + ' result route'"
            [attr.aria-pressed]="isResultSelected(row)" [disabled]="interactionBlocked || !resultSelectable(row)" (click)="selectResult(row)">
            <strong>{{ readableName(row.commandOutcome) || 'Unavailable result' }}</strong>
            <span>{{ resultSummary(row) }}</span>
          </button>
          <p *ngIf="!resultRows.length">No readable result mapping is available. Original activity settings are preserved.</p>
        </nav>
        </ui-panel>
        <ng-container *ngIf="selectedResult as row; else unavailableSelection">
        <ng-template #resultDetailHeader><h5>{{ readableName(row.commandOutcome) }}</h5></ng-template>
        <ui-panel [headerTemplate]="resultDetailHeader" [noGrow]="true" class="activity-result-panel"
          [attr.data-automated-result-detail]="row.commandOutcome" [attr.aria-label]="readableName(row.commandOutcome) + ' result details'">
          <div class="activity-result-detail">
          <p *ngIf="resultIssue(row)" role="status">{{ resultIssue(row) }}</p>
          <ng-container *ngIf="row.outcome as outcome">
            <label>Destination<select uiControl #destination="ngModel" data-automated-destination
              [attr.aria-label]="row.commandOutcome + ' destination'" [ngModel]="outcome.destinationNodeId"
              (ngModelChange)="changeDestination(row, $event, destination)" [ngModelOptions]="standalone" [disabled]="!resultEditable(row)">
              <option value="">Choose a destination</option>
              <option *ngIf="outcome.destinationNodeId && resultIssue(row).startsWith('Unavailable saved destination')" [value]="outcome.destinationNodeId">{{ resultIssue(row) }}</option>
              <option *ngFor="let node of destinations" [value]="node.id">{{ node.displayName || node.stepCode || (node.typeCode === 'terminal' ? 'Complete workflow · ' + node.id : node.id) }}</option>
            </select></label>
            <p>{{ resultSummary(row) }} when this activity returns {{ readableName(row.commandOutcome) }}.</p>
            <details data-automated-route-identity>
              <summary>Route identity · {{ outcome.routeOutcome || 'Not set' }}</summary>
              <p>Activity output: <code>{{ row.commandOutcome }}</code>. This routing key is authored; changing it does not rename the activity output.</p>
              <label>Route key<input uiControl #routeKey="ngModel" data-automated-route-key maxlength="120"
                [attr.aria-label]="row.commandOutcome + ' route key'" [ngModel]="outcome.routeOutcome"
                (ngModelChange)="changeRouteKey(row, $event, routeKey)" [ngModelOptions]="standalone" [disabled]="!resultEditable(row)" /></label>
            </details>
          </ng-container>
          <button uiButton [text]="true" type="button" (click)="reviewActivity()" [disabled]="interactionBlocked">Review activity settings</button>
          </div>
        </ui-panel>
        </ng-container>
        <ng-template #unavailableSelection><p class="activity-result-detail">The selected result is missing or ambiguous. Choose a current result to inspect it.</p></ng-template>
      </div>
    </section>
    <div class="activity-actions" [hidden]="section() === 'settings'">
      <button
        uiButton
        type="button"
        (click)="applyActivityChanges()"
        [disabled]="!pending || settingsDisabled"
      >
        Apply activity settings</button
      ><button
        uiButton
        type="button"
        (click)="discardActivityChanges()"
        [disabled]="!pending || interactionBlocked"
      >
        Discard local activity edits
      </button>
    </div>
    <p *ngIf="pending" role="status">
      Activity edits have not yet been applied to the draft.
    </p>
  </section>
